import { getFunctions, httpsCallable } from 'firebase/functions'
import { auth, db } from '../../../firebase/config'
import { DATABASE_NAME } from '@env'
import {
	collection,
	doc,
	query,
	where,
	orderBy,
	onSnapshot,
	limit,
	startAfter,
} from 'firebase/firestore'
export enum ORDER_STATUS_LIST_ENUM {
	Completed = 'Completed',
	Accepted = 'Accepted',
	inProgress = 'In Progress',
	readyForPickup = 'Ready For Pickup',
	outForDelivery = 'Out for Delivery',
	refunded = 'Refunded',
	cancelled = 'Cancelled',
}
export enum DOORDASH_ORDER_STATUS_LIST_ENUM {
	created = 'created',
	DASHER_CONFIRMED = 'DASHER_CONFIRMED',
	DASHER_CONFIRMED_PICKUP_ARRIVAL = 'DASHER_CONFIRMED_PICKUP_ARRIVAL',
	DASHER_PICKED_UP = 'DASHER_PICKED_UP',
	DASHER_CONFIRMED_DROPOFF_ARRIVAL = 'DASHER_CONFIRMED_DROPOFF_ARRIVAL',
	DASHER_DROPPED_OFF = 'DASHER_DROPPED_OFF',
	DELIVERY_CANCELLED = 'DELIVERY_CANCELLED',
	DASHER_ENROUTE_TO_DROPOFF = 'DASHER_ENROUTE_TO_DROPOFF',
	DASHER_ENROUTE_TO_PICKUP = 'DASHER_ENROUTE_TO_PICKUP',
}
export const ORDER_STATUS_LIST = [
	ORDER_STATUS_LIST_ENUM.Completed,
	ORDER_STATUS_LIST_ENUM.Accepted,
	ORDER_STATUS_LIST_ENUM.inProgress,
	ORDER_STATUS_LIST_ENUM.readyForPickup,
	ORDER_STATUS_LIST_ENUM.outForDelivery,
	ORDER_STATUS_LIST_ENUM.refunded,
	ORDER_STATUS_LIST_ENUM.cancelled,
]

type SubscribeToUserAllOrdersType = {
	unsubscribeRef: React.MutableRefObject<() => void>
	loadMore?: boolean
	mode?: string | null
	filterType?: string
	lastOrderDate?: Date | null
	setLastOrderDate: (lastOrderDate: any) => void
	setLoading: (val: boolean) => void
	setOrdersView: any
	setHasMoreOrders: (hasMoreOrders: boolean) => void
	batchSize?: number
	onComplete?: () => void
	isLoadingMore?: boolean
}

export const subscribeToPOSOrders = ({
	loadMore = false,
	mode = null,
	filterType,
	lastOrderDate,
	setLastOrderDate,
	setLoading,
	setOrdersView,
}) => {
	setLoading(true)
	const functions = getFunctions()
	const filterMode = mode || filterType
	if (auth.currentUser !== null) {
		// Call the cloud function
		const getAllOrdersForPOS = httpsCallable(functions, 'getAllOrdersForPOS')

		getAllOrdersForPOS({
			orgId: global.org,
			limit: 10,
			startAfter: loadMore ? lastOrderDate : null, // Use the last order date for pagination if loading more
		})
			.then(result => {
				const { orders }: any = result.data

				if (!orders || orders.length === 0) {
					if (!loadMore) setOrdersView([]) // No orders found
					setLoading(false)
					return
				}

				const newOrders = []
				let pos = 0

				orders.forEach(data => {
					const items = data.orderItems.map(orderItem => {
						const isGiftCard = orderItem.name == 'Gift Card'
						const mods = isGiftCard
							? []
							: orderItem.modifiers.map(itemMod => ({
									name: itemMod?.name,
									price: itemMod.priceMoney.amount / 100,
								}))

						return {
							name: orderItem?.name,
							qty: orderItem.quantity,
							mods: mods,
							note: orderItem.note,
							total: orderItem.priceMoney.amount,
							id: orderItem.id,
							rId: orderItem.restaurantId,
							noPromo: orderItem.priceMoney.amount,
						}
					})

					const orderObj = {
						pos: pos,
						orderDate: {
							seconds: data.date?._seconds || 0,
						},
						pickupDate: {
							seconds: data.pickupDate?._seconds || 0,
						},
						orderID: data.id,
						orderNote: data.note,
						orderItems: items,
						prepTime: data.prepTime,
						restaurants: items.map(item => item.rId),
						prices: data.transaction,
						pickupStatus: data.status,
						orderNumber: data.number,
						rawObject: data,
						deliveryStatus: data.delivery?.status || '',
						deliveryTrackingURL: data.delivery?.trackingUrl || '',
						scheduled: data.scheduled ?? false,
					}

					if (filterMode === 'pos_only') {
						if (data.userId === auth.currentUser.uid) {
							console.log(data.userId)
							console.log(data.channelData.appType)
							newOrders.push(orderObj)
							pos++
						}
					} else if (filterMode === 'app_only') {
						if (['kiosk', 'mobile', 'web', 'pos'].includes(data.channelData?.appType)) {
							newOrders.push(orderObj)
							pos++
						}
					} else {
						newOrders.push(orderObj)
						pos++
					}
				})

				if (loadMore) {
					setOrdersView(prevOrders => [...prevOrders, ...newOrders])
				} else {
					setOrdersView(newOrders)
				}

				setLastOrderDate(orders[orders.length - 1].orderDate) // Update lastOrderDate for pagination
				setLoading(false)
			})
			.catch(error => {
				console.error('Error fetching orders from cloud function: ', error)
				setLoading(false)
			})
	}
}

export const subscribeToUserAllOrders = async ({
	unsubscribeRef,
	setOrdersView,
	setLoading,
	lastOrderDate = null,
	isLoadingMore = false,
	setLastOrderDate,
	setHasMoreOrders,
	batchSize = 10,
	filterType = 'default',
	onComplete = () => null,
}: SubscribeToUserAllOrdersType) => {
	if (auth.currentUser !== null) {
		const orderRef = collection(doc(db, DATABASE_NAME, global.org), 'Orders')
		const userId = auth.currentUser.uid

		let orderQuery
		if (lastOrderDate) {
			// If we have a last order date, start after that date
			orderQuery = query(
				orderRef,
				where('status', 'in', ORDER_STATUS_LIST),
				orderBy('date', 'desc'),
				startAfter(lastOrderDate),
				limit(batchSize)
			)
		} else {
			// Initial load - only fetch first batch
			orderQuery = query(
				orderRef,
				where('status', 'in', ORDER_STATUS_LIST),
				orderBy('date', 'desc'),
				limit(batchSize)
			)
		}

		// Set up the Firestore onSnapshot listener
		unsubscribeRef.current = await onSnapshot(
			orderQuery,
			snapshot => {
				if (snapshot.empty) {
					if (!isLoadingMore) {
						setOrdersView([]) // No orders found
					}
					setLoading(false)
					setHasMoreOrders(false)
					return
				}

				const newOrders = []
				let pos = 0

				snapshot.docs.forEach(doc => {
					const data = doc.data()

					// if (data.status !== 'Pending' && data.status !== 'Failed') {
					const items = data.orderItems.map(orderItem => {
						const isGiftCard = orderItem.name == 'Gift Card'
						const mods = isGiftCard
							? []
							: orderItem.modifiers.map(itemMod => ({
									name: itemMod?.name,
									price: itemMod.priceMoney.amount / 100,
								}))

						return {
							name: orderItem?.name,
							qty: orderItem.quantity,
							mods: mods,
							note: orderItem.note,
							total: orderItem.priceMoney.amount,
							id: orderItem.id,
							rId: orderItem.restaurantId,
							noPromo: orderItem.priceMoney.amount,
						}
					})

					const orderObj = {
						pos: pos,
						orderDate: data.date,
						pickupDate: data.pickupDate,
						orderID: data.id,
						orderNote: data.note,
						orderItems: items,
						prepTime: data.prepTime,
						restaurants: items.map(item => item.rId),
						prices: data.transaction,
						pickupStatus: data.status,
						orderNumber: data.number,
						rawObject: data,
						deliveryStatus: data.delivery?.status || '',
						deliveryTrackingURL: data.delivery?.trackingUrl || '',
						scheduled: data.scheduled ?? false,
					}

					// Filter orders based on appType
					// if (filterType === 'default') {
					newOrders.push(orderObj)
					pos++
					// } else if (data.source?.type === 'pos') {
					// 	newOrders.push(orderObj)
					// 	pos++
					// } else if (filterType === 'web' && data.source?.type === 'web') {
					// 	newOrders.push(orderObj)
					// 	pos++
					// } else if (filterType === 'kiosk' && data.source?.type === 'kiosk') {
					// 	newOrders.push(orderObj)
					// 	pos++
					// }
					// } else {
					// 	console.log('pending or failed')
					// }
				})

				// Process orders in batches
				if (isLoadingMore) {
					// Append new orders to existing list
					setOrdersView(prevOrders => [...prevOrders, ...newOrders])

					// onComplete()
				} else {
					// For initial load, only set the first batch
					setOrdersView(newOrders)
				}

				// Update last order date for pagination
				if (newOrders.length > 0) {
					setLastOrderDate(newOrders[newOrders.length - 1].orderDate)
					// Only set hasMoreOrders to true if we received a full batch
					setHasMoreOrders(newOrders.length === batchSize)
				} else {
					setHasMoreOrders(false)
				}

				setLoading(false)
			},
			error => {
				console.error('Error listening to orders: ', error)
				setLoading(false)
			}
		)
	}
}

export const subscribeToUserWebOrders = ({ unsubscribeRef, setOrdersView, setLoading }) => {
	if (auth.currentUser !== null) {
		const orderRef = collection(doc(db, DATABASE_NAME, global.org), 'Orders')
		const userId = auth.currentUser.uid

		const orderQuery = query(orderRef, where('customer.uid', '==', userId), orderBy('date', 'desc'))

		// Set up the Firestore onSnapshot listener
		unsubscribeRef.current = onSnapshot(
			orderQuery,
			snapshot => {
				if (snapshot.empty) {
					setOrdersView([]) // No orders found
					setLoading(false)
					return
				}

				const newOrders = []
				let pos = 0

				snapshot.forEach(doc => {
					const data = doc.data()

					if (data.status !== 'Pending' && data.status !== 'Failed') {
						const items = data.orderItems.map(orderItem => {
							const isGiftCard = orderItem.name == 'Gift Card'
							const mods = isGiftCard
								? []
								: orderItem.modifiers.map(itemMod => ({
										name: itemMod?.name,
										price: itemMod.priceMoney.amount / 100,
									}))

							return {
								name: orderItem?.name,
								qty: orderItem.quantity,
								mods: mods,
								note: orderItem.note,
								total: orderItem.priceMoney.amount,
								id: orderItem.id,
								rId: orderItem.restaurantId,
								noPromo: orderItem.priceMoney.amount,
							}
						})

						const orderObj = {
							pos: pos,
							orderDate: data.date,
							pickupDate: data.pickupDate,
							orderID: data.id,
							orderNote: data.note,
							orderItems: items,
							prepTime: data.prepTime,
							restaurants: items.map(item => item.rId),
							prices: data.transaction,
							pickupStatus: data.status,
							orderNumber: data.number,
							rawObject: data,
							deliveryStatus: data.delivery?.status || '',
							deliveryTrackingURL: data.delivery?.trackingUrl || '',
							scheduled: data.scheduled ?? false,
						}

						newOrders.push(orderObj)
						pos++
					}
				})

				setOrdersView(newOrders)
				setLoading(false)
			},
			error => {
				console.error('Error listening to orders: ', error)
				setLoading(false)
			}
		)
	}
}
